import { Environment } from '@/config';
import { useApiEnvStore } from '@utils/api-client';

export enum FEATURE_FLAG {
  QUOTE_SIDEBAR_DRAG = 'quoteSidebarDrag',
  QUOTE_UW_TOOLKIT_FULLSCREEN = 'quoteUwToolkitFullscreen',
  AGENCY_BILL_STATEMENT = 'agencyBillStatement',
  POLICY_CHAT = 'PolicyChat',
  FORMS_V2 = 'formsV2',
  UW_RULE_APPLY_TO_DB = 'uwRuleApplyToDb',
}

export type ToggleableFlag = {
  flag: FEATURE_FLAG;
  label: string;
  description: string;
  /** Environments where this flag can be toggled at runtime */
  toggleableIn: Environment[];
};

export const TOGGLEABLE_FLAGS: ToggleableFlag[] = [];

/** Returns the toggleable flags available in the current environment. */
export const getToggleableFlags = (): ToggleableFlag[] => {
  const env = window.lio.getApiEnvironment();
  return TOGGLEABLE_FLAGS.filter((f) => f.toggleableIn.includes(env));
};

export const getIsFeatureFlagEnabled = (flag: FEATURE_FLAG, opts?: { isInternalUser: boolean }) => {
  const { isInternalUser } = opts ?? { isInternalUser: false };

  // Check for a runtime override from the store
  const overrides = useApiEnvStore.getState().featureFlagOverrides;
  if (flag in overrides) {
    return overrides[flag];
  }

  switch (flag) {
    case FEATURE_FLAG.QUOTE_SIDEBAR_DRAG:
      if (isInternalUser) {
        // internal users can use drag in all environments
        return true;
      }

      // currently disabled for all other users in every environment
      return false;
    case FEATURE_FLAG.QUOTE_UW_TOOLKIT_FULLSCREEN:
      if (isInternalUser) {
        // only internal users get toolkit fullscreen
        return true;
      }

      // currently disabled for all other users in every environment
      return false;
    case FEATURE_FLAG.AGENCY_BILL_STATEMENT:
      return true;
    case FEATURE_FLAG.POLICY_CHAT:
      return true;
    case FEATURE_FLAG.FORMS_V2: {
      if (!isInternalUser) return false;
      const env = window.lio.getApiEnvironment();
      return env !== Environment.Prod;
    }
    case FEATURE_FLAG.UW_RULE_APPLY_TO_DB: {
      // Hard-blocked in production — direct DB apply is not yet available in prod.
      const env = window.lio.getApiEnvironment();
      if (env === Environment.Prod) return false;
      return isInternalUser;
    }
    default:
      throw new Error(`Unknown feature flag: ${flag}`);
  }
};
